import { useTranslation } from "react-i18next"
import { Tag } from "@/components/ui/tag"
import { BetterDocField } from "@/modules/admin/better-doc/components/better-doc-field"
import { BetterDocRemoteMedicTable } from "@/modules/admin/better-doc/components/better-doc-remote-medic-table"
import { BetterDocRemoteRequestStatus } from "@/modules/admin/better-doc/components/better-doc-remote-request-status"
import { BetterDocSessionLink } from "@/modules/admin/better-doc/components/better-doc-session-link"
import { EMPTY_VALUE_PLACEHOLDER } from "@/modules/admin/better-doc/constants/better-doc-constants"
import { getBetterDocEnumLabel } from "@/modules/admin/better-doc/lib/better-doc-enum-label"
import {
  formatBetterDocDate,
  formatBetterDocDateTime,
  formatBetterDocValue,
} from "@/modules/admin/better-doc/lib/format-better-doc-value"
import { BetterDocRemoteRecommendationResult } from "@/modules/admin/better-doc/services/betterDocRemoteRecommendationQuery"

type BetterDocRecommendationDetailsProps = {
  result: BetterDocRemoteRecommendationResult
}

export const BetterDocRecommendationDetails = ({ result }: BetterDocRecommendationDetailsProps) => {
  const { t } = useTranslation()
  const { recommendation } = result

  const formatTimeFrame = () => {
    if (recommendation === null) {
      return EMPTY_VALUE_PLACEHOLDER
    }

    const { timeFrameStartingAt, timeFrameEndingAt } = recommendation
    if (timeFrameStartingAt === null && timeFrameEndingAt === null) {
      return EMPTY_VALUE_PLACEHOLDER
    }

    return `${formatBetterDocDate(timeFrameStartingAt)} - ${formatBetterDocDate(timeFrameEndingAt)}`
  }

  const formatSelection = () => {
    if (recommendation === null) {
      return EMPTY_VALUE_PLACEHOLDER
    }

    const selectionParts = [recommendation.selectionType, recommendation.selectionId].filter(
      (selectionPart) => selectionPart !== null && selectionPart !== "",
    )

    return selectionParts.length === 0 ? EMPTY_VALUE_PLACEHOLDER : selectionParts.join(" ")
  }

  return (
    <div className="flex flex-col gap-4" data-testid="better-doc-admin-recommendation-details">
      <BetterDocRemoteRequestStatus
        status={result.status}
        httpStatusCode={result.httpStatusCode}
        errorReason={result.errorReason}
        exchangeId={result.exchangeId}
      />
      {recommendation === null ? null : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.recommendationId")}
              value={formatBetterDocValue(recommendation.recommendationId)}
              testId="better-doc-admin-recommendation-recommendation-id"
              copyText={recommendation.recommendationId ?? undefined}
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.state")}
              value={
                recommendation.state === null ? (
                  EMPTY_VALUE_PLACEHOLDER
                ) : (
                  <Tag hasIcon={false} label={getBetterDocEnumLabel(t, "recommendationState", recommendation.state)} />
                )
              }
              testId="better-doc-admin-recommendation-state"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.recommendationType")}
              value={
                recommendation.recommendationType === null
                  ? EMPTY_VALUE_PLACEHOLDER
                  : getBetterDocEnumLabel(t, "recommendationType", recommendation.recommendationType)
              }
              testId="better-doc-admin-recommendation-type"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.caseId")}
              value={formatBetterDocValue(recommendation.caseId)}
              testId="better-doc-admin-recommendation-case-id"
              copyText={recommendation.caseId ?? undefined}
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.reason")}
              value={formatBetterDocValue(recommendation.reason)}
              testId="better-doc-admin-recommendation-reason"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.timeFrame")}
              value={formatTimeFrame()}
              testId="better-doc-admin-recommendation-time-frame"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.selection")}
              value={formatSelection()}
              testId="better-doc-admin-recommendation-selection"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.insertedAt")}
              value={formatBetterDocDateTime(recommendation.insertedAt)}
              testId="better-doc-admin-recommendation-inserted-at"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.updatedAt")}
              value={formatBetterDocDateTime(recommendation.updatedAt)}
              testId="better-doc-admin-recommendation-updated-at"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.secondOpinionVisitConfirmedByPatientAt")}
              value={formatBetterDocDateTime(recommendation.secondOpinionVisitConfirmedByPatientAt)}
              testId="better-doc-admin-recommendation-second-opinion-visit-confirmed-by-patient-at"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.secondOpinionVisitConfirmedAt")}
              value={formatBetterDocDateTime(recommendation.secondOpinionVisitConfirmedAt)}
              testId="better-doc-admin-recommendation-second-opinion-visit-confirmed-at"
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.partnerPatientId")}
              value={formatBetterDocValue(recommendation.partnerPatientId)}
              testId="better-doc-admin-recommendation-partner-patient-id"
              copyText={recommendation.partnerPatientId ?? undefined}
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.partnerSessionId")}
              value={formatBetterDocValue(recommendation.partnerSessionId)}
              testId="better-doc-admin-recommendation-partner-session-id"
              copyText={recommendation.partnerSessionId ?? undefined}
            />
            <BetterDocField
              label={t("admin.betterDoc.recommendationDetails.sofiaSession")}
              value={
                recommendation.localSessionId === null ? (
                  t("admin.betterDoc.recommendationDetails.notInThisEnvironment")
                ) : (
                  <BetterDocSessionLink sessionId={recommendation.localSessionId} />
                )
              }
              testId="better-doc-admin-recommendation-sofia-session"
            />
          </div>
          <BetterDocRemoteMedicTable medics={recommendation.medics} />
        </>
      )}
    </div>
  )
}
